import { useEffect, useMemo, useState } from 'react';

import SiteHeader from '@/components/layout/SiteHeader/SiteHeader';
import SiteFooter from '@/components/layout/SiteFooter/SiteFooter';
import BrandLine from '@/components/layout/BrandLine/BrandLine';
import ReportCover from '@/components/shirabe/ReportCover/ReportCover';
import ChapterNav from '@/components/shirabe/ChapterNav/ChapterNav';
import { ChapterNumberingProvider } from '@/components/shirabe/ChapterSection/ChapterNumbering';
import VerdictCard from '@/components/shirabe/VerdictCard/VerdictCard';
import ChapterOverview from '@/components/shirabe/chapters/ChapterOverview';
import ChapterHazards from '@/components/shirabe/chapters/ChapterHazards';
import ChapterPrice from '@/components/shirabe/chapters/ChapterPrice';
import ChapterEnvironment from '@/components/shirabe/chapters/ChapterEnvironment';
import ChapterProspects from '@/components/shirabe/chapters/ChapterProspects';
import ChapterBuilding from '@/components/shirabe/chapters/ChapterBuilding';
import ChapterGround from '@/components/shirabe/chapters/ChapterGround';
import ChapterSummary from '@/components/shirabe/chapters/ChapterSummary';

import { api } from '@/lib/api';
import type { SiteAnalysisResponse } from '@/lib/api.types';
import { hasProspectsData, visibleChaptersFor } from '@/lib/chapters';
import { useActiveChapter } from '@/hooks/useActiveChapter';
import { usePageSeo } from '@/hooks/usePageSeo';

import styles from './ShirabeSample.module.css';

export default function ShirabeSample() {
  usePageSeo({ canonical: '/report/sample' });
  const [analysis, setAnalysis] = useState<SiteAnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .sampleReport()
      .then((res) => {
        if (!cancelled) setAnalysis(res);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'サンプルの読み込みに失敗しました。');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasProspects = analysis ? hasProspectsData(analysis) : false;
  const visibleChapters = useMemo(() => visibleChaptersFor(analysis), [analysis]);
  const chapterIds = useMemo(
    () => (analysis ? visibleChapters.map((c) => c.id) : []),
    [analysis, visibleChapters],
  );
  const activeId = useActiveChapter(chapterIds);

  return (
    <>
      <SiteHeader variant="app" />
      <main className={styles.main}>
        <section className={styles.heroStrip}>
          <div className={styles.heroInner}>
            <div className={styles.eyebrow}>SAMPLE ── 立地レポート</div>
            <h1 className={styles.title}>
              これが「立地レポート」の見本です。
            </h1>
            <p className={styles.desc}>
              実際のレポートのかわりに、サンプル住所のデータでお届けします。8 つの章を順に読んでみてください。
            </p>
          </div>
        </section>

        <section className={styles.coverWrap}>
          <ReportCover
            address={analysis?.address}
            generatedAt={analysis?.generated_at}
            preview
          />
        </section>

        {error && (
          <section className={styles.errorBox}>
            <p>{error}</p>
            <p>
              バックエンドが起動しているか、API Base 設定 (
              <code>?dev=1</code>) を確認してください。
            </p>
          </section>
        )}

        {analysis && (
          <div className={styles.reportLayout}>
            <div className={styles.navRail}>
              <ChapterNav chapters={visibleChapters} activeId={activeId} />
            </div>
            <div className={styles.chapters}>
              <BrandLine />
              <VerdictCard analysis={analysis} />
              {/* 章の並びは lib/chapters.ts の CHAPTERS と一致させること(番号はそこから導出)。 */}
              <ChapterNumberingProvider chapters={visibleChapters}>
                <ChapterOverview analysis={analysis} />
                <ChapterHazards portfolio={analysis.hazards} site={analysis.site} />
                <ChapterGround ground={analysis.ground} site={analysis.site} />
                <ChapterBuilding portfolio={analysis.planning} site={analysis.site} />
                <ChapterPrice portfolio={analysis.price} site={analysis.site} />
                <ChapterEnvironment portfolio={analysis.life_environment} site={analysis.site} />
                {hasProspects && (
                  <ChapterProspects
                    portfolio={analysis.life_environment}
                    planning={analysis.planning}
                    site={analysis.site}
                  />
                )}
                <ChapterSummary analysis={analysis} />
              </ChapterNumberingProvider>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
